import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IS_PRODUCTION_DEPLOYMENT } from '../../app/shell/TestEnvStrip';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Lock } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { orgApi } from '../../api/endpoints';
import { fieldErrors, issuesOf } from '../../api/errors';
import { currenciesQuery, queryKeys } from '../../api/queries';
import type { EtaEnvironment } from '../../api/types';
import { AppShell } from '../../app/shell/AppShell';
import { useI18n } from '../../i18n/I18nProvider';
import { Button, ButtonLink } from '../../ui/Button';
import { RadioCards } from '../../ui/Choice';
import { ComboBoxField } from '../../ui/ComboBox';
import { Callout } from '../../ui/Feedback';
import { FormErrors } from '../../ui/FormErrors';
import { Page, PageHeader, Panel, layoutStyles, useDocumentTitle } from '../../ui/Layout';
import { TextField } from '../../ui/TextField';
import { useToast } from '../../ui/Toast';
import { describeFrom, optionalText, summaryItems } from '../forms';
import styles from './Orgs.module.css';

export function CreateOrgPage() {
  const { t, pick } = useI18n();
  useDocumentTitle(t.orgs.createTitle);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const currencies = useQuery(currenciesQuery);

  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [rin, setRin] = useState('');
  const [environment, setEnvironment] = useState<EtaEnvironment>('mock');
  const [currency, setCurrency] = useState<string | null>('EGP');

  const create = useMutation({
    mutationFn: () =>
      orgApi.create({
        name: name.trim(),
        legalName: optionalText(legalName),
        rin: optionalText(rin),
        etaEnvironment: environment,
        baseCurrency: currency ?? 'EGP',
      }),
    onSuccess: async ({ id }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      toast.success(t.orgs.created);
      await navigate({ to: '/orgs/$orgId', params: { orgId: id } });
    },
    onError: (error) => {
      if (issuesOf(error).length === 0) toast.error(error);
    },
  });

  const labels = {
    name: t.orgs.name,
    legalName: t.orgs.legalName,
    rin: t.orgs.rin,
    etaEnvironment: t.orgs.environment,
    baseCurrency: t.orgs.baseCurrency,
  };
  const issues = issuesOf(create.error);
  const errors = fieldErrors(t, issues);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <AppShell>
      <Page narrow>
        <PageHeader
          eyebrow={
            <>
              <Building2 size={14} aria-hidden="true" />
              {t.orgs.eyebrow}
            </>
          }
          title={t.orgs.createTitle}
          subtitle={t.orgs.createSubtitle}
        />
        <form onSubmit={onSubmit} noValidate className={layoutStyles.stack}>
          <FormErrors items={summaryItems(t, issues, describeFrom(labels))} />
          <Panel title={t.orgs.identitySection}>
            <div className={styles.formGrid}>
              <TextField
                label={t.orgs.name}
                fieldName="name"
                value={name}
                onChange={setName}
                errorMessage={errors.name}
                isRequired
                autoFocus
              />
              <TextField
                label={t.orgs.legalName}
                fieldName="legalName"
                value={legalName}
                onChange={setLegalName}
                description={t.orgs.legalNameHint}
                errorMessage={errors.legalName}
                isOptional
              />
              <TextField
                label={t.orgs.rin}
                fieldName="rin"
                value={rin}
                onChange={setRin}
                inputMode="numeric"
                dir="ltr"
                maxLength={9}
                description={t.orgs.rinHint}
                errorMessage={errors.rin}
                isRequired={environment === 'production'}
                isOptional={environment !== 'production'}
              />
              <ComboBoxField
                label={t.orgs.baseCurrency}
                fieldName="baseCurrency"
                options={(currencies.data ?? []).map((c) => ({
                  id: c.code,
                  label: `${c.code} — ${pick(c.nameAr, c.nameEn)}`,
                }))}
                value={currency}
                onChange={setCurrency}
                errorMessage={errors.baseCurrency}
                isRequired
              />
            </div>
          </Panel>
          <Panel title={t.orgs.environment} description={t.orgs.environmentIntro}>
            <RadioCards<EtaEnvironment>
              label={t.orgs.environment}
              fieldName="etaEnvironment"
              value={environment}
              onChange={setEnvironment}
              errorMessage={errors.etaEnvironment}
              options={[
                {
                  value: 'mock',
                  title: t.env.mock,
                  description: t.orgs.envDescriptions.mock,
                },
                // A test or preview deployment offers the mock environment only (council tax memo
                // 2026-10-04); the API refuses anything else there as well.
                ...(IS_PRODUCTION_DEPLOYMENT
                  ? ([
                      {
                        value: 'preprod',
                        title: t.env.preprod,
                        description: t.orgs.envDescriptions.preprod,
                      },
                      {
                        value: 'production',
                        title: t.env.production,
                        description: t.orgs.envDescriptions.production,
                      },
                    ] as const)
                  : []),
              ]}
            />
            <Callout tone="warning" title={t.orgs.environmentLockedTitle}>
              <span style={{ display: 'inline-flex', gap: '0.4em', alignItems: 'center' }}>
                <Lock size={14} aria-hidden="true" />
                {t.orgs.environmentLocked}
              </span>
            </Callout>
          </Panel>
          <div className={layoutStyles.formActions}>
            <ButtonLink to="/orgs" variant="secondary">
              {t.common.cancel}
            </ButtonLink>
            <Button type="submit" variant="primary" isPending={create.isPending}>
              {t.orgs.create}
            </Button>
          </div>
        </form>
      </Page>
    </AppShell>
  );
}
